// src/utils/adminUtils.ts

import type { AccessPolicy } from '../models/types';
import type { ManagedUser } from '../models/adminTypes';

/**
 * Parse the cognito:groups claim from a JWT ID token.
 * Returns the list of groups the user belongs to.
 */
export function parseGroupsFromToken(idToken: string): string[] {
  try {
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    const groups = payload['cognito:groups'];
    if (Array.isArray(groups)) return groups;
    if (typeof groups === 'string') return groups.split(',').map(g => g.trim());
    return [];
  } catch {
    return [];
  }
}

/**
 * Check if the ID token indicates admin group membership.
 */
export function isAdmin(idToken: string): boolean {
  return parseGroupsFromToken(idToken).includes('admins');
}

/**
 * Format an access policy into a human-readable summary.
 * Returns "All folders" for wildcard, "No access" for empty, or "X years, Y events" for specific.
 */
export function formatAccessSummary(policy: AccessPolicy | null): string {
  if (!policy) return 'No access';

  const hasWildcard = policy.allowedYears.includes('*') ||
                      policy.fullYearAccess.includes('*');
  if (hasWildcard) return 'All folders';

  const yearCount = policy.fullYearAccess.length;
  const eventCount = Object.values(policy.allowedEvents)
    .reduce((sum, events) => sum + events.length, 0);

  if (yearCount === 0 && eventCount === 0) return 'No access';

  const parts: string[] = [];
  if (yearCount > 0) parts.push(`${yearCount} year${yearCount !== 1 ? 's' : ''}`);
  if (eventCount > 0) parts.push(`${eventCount} event${eventCount !== 1 ? 's' : ''}`);
  return parts.join(', ');
}

/**
 * Filter users by search text (case-insensitive email match).
 */
export function filterUsersBySearch(
  users: ManagedUser[],
  searchText: string
): ManagedUser[] {
  if (!searchText.trim()) return users;
  const lower = searchText.toLowerCase();
  return users.filter(user => user.email.toLowerCase().includes(lower));
}

/**
 * Validate email format.
 */
export function isValidEmail(email: string): boolean {
  if (!email || !email.trim()) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
}

/**
 * Check if a user is trying to delete themselves.
 */
export function isSelfDelete(currentUserSub: string, targetUserSub: string): boolean {
  return currentUserSub === targetUserSub;
}
